import { usePermissao } from "@/utils/permissao";
import { Badge } from "@/components/ui/cores";
import { getStatusSinistroLabel, StatusSinistroEnum } from "@/types/enum/StatusSinistro";
import { EntityIcon } from "@/components/ui/cores";
import { PageHeader } from "@/components/ui/page-header";
import { Column, DataTable, Pagination } from "@/components/datatable/DataTable";
import { ExportarButton, TODOS_REGISTROS } from "@/components/datatable/ExportarButton";
import Filter from "@/components/datatable/Filter";
import { ActionButton, Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { SinistroService } from "@/services";
import { PageRequest, PaginationData } from "@/types/pagination";
import { LoaderCircle, Pencil, Plus, Trash2Icon, Eye } from "lucide-react";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useRef, useState } from "react";
import { SinistroLista } from "@/types/SinistroDTO";
import { Sort } from "@/types/sort";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { ErrorPage } from "../ErrorPage";
import { getTenant } from "@/utils/TenantUtils";

export const SinistroListPage = () => {
  const [data, setData] = useState<SinistroLista[]>([]);
  const [pagination, setPagination] = useState<PaginationData>();
  const [error, setError] = useState<AxiosError>();
  const [excluindo, setExcluindo] = useState<SinistroLista>();
  const [removendo, setRemovendo] = useState(false);
  const navigate = useNavigate();
  const podeAlterar = usePermissao('SEGURO_WRITE');

  const ultimaBusca = useRef<[string?, PageRequest?, Sort?]>([]);

  const columns: Column[] = [
    {field: 'id', show: false, label: 'ID'},
    {field: 'data', show: true, label: 'Data', type: 'DATE', sorted: true},
    {field: 'seguro.segurado.nome', show: true, label: 'Segurado', filter: true},
    {field: 'seguro.placa', show: true, label: 'Placa', filter: true},
    {field: 'seguro.seguradora.nome', show: true, label: 'Seguradora', filter: true},
    {field: 'aviso', show: true, label: 'Aviso', filter: true},
    {field: 'status', show: true, label: 'Status', filter: true, type: 'CUSTOM', exportValue: item => getStatusSinistroLabel(item.status), getValue: item => {
      const status = typeof item === 'string' ? item : item.status;
      return status && <Badge className={status === StatusSinistroEnum.pendente ? 'bg-rose-500/10 text-rose-700 dark:text-rose-300' : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'}>{getStatusSinistroLabel(status)}</Badge>;
    }},
  ]

  const excluir = () => {
    setRemovendo(true);
    SinistroService.remove(excluindo!.id!)
      .then(() => {
        toast.success('Sinistro excluído.');
        setExcluindo(undefined);
        onSearch(...ultimaBusca.current);
      })
      .catch(() => {})
      .finally(() => setRemovendo(false));
  }

  const onSearch = (filterRsql?: string, pagination?: PageRequest, sort?: Sort) => {
    ultimaBusca.current = [filterRsql, pagination, sort];
    SinistroService.findLista(filterRsql, pagination, sort)
      .then(response => {
        setData(response.data.content)
        setPagination({
          ...response.data
        })
      })
      .catch(setError);
  }

  if (error){
    return <ErrorPage axiosError={error} />
  }

  return (
    <div className="space-y-4">
    <PageHeader icon={<EntityIcon entidade="sinistro" />} title="Sinistros" total={pagination?.totalElements}>
      <ExportarButton arquivo="sinistros" columns={columns} buscar={() => SinistroService.findLista(ultimaBusca.current[0], TODOS_REGISTROS, ultimaBusca.current[2])} />
      {podeAlterar && <Button onClick={() => navigate('novo')}><Plus className="mr-2 h-4 w-4" /> Novo sinistro</Button>}
    </PageHeader>
    <Card className="overflow-hidden shadow-none">
      <Filter columns={columns} onSearch={onSearch} />
      <DataTable values={data} 
        columns={columns} 
        keyExtractor={(item: any, index: number) => item.id} 
        pagination={pagination}
        actions={(item: any) => {
          return (
            <>
            <ActionButton icon={podeAlterar ? <Pencil className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />} label={podeAlterar ? "Editar" : "Visualizar"} onClick={() => navigate(`/${getTenant()}/seguros/${item.seguro.id}/sinistro/${item.id}`)} />
            {podeAlterar && <ActionButton icon={<Trash2Icon className="h-3.5 w-3.5" />} label="Excluir" onClick={() => setExcluindo(item)} variant="destructive" />}
            </>
          )
        }}
      />
      <Pagination pagination={pagination} />
    </Card>
    <Dialog open={!!excluindo} onOpenChange={open => !open && !removendo && setExcluindo(undefined)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Excluir sinistro?</DialogTitle>
          <DialogDescription>
            O sinistro de <span className="font-medium text-foreground">{excluindo?.seguro?.segurado?.nome}</span>
            {excluindo?.aviso && <> (aviso {excluindo.aviso})</>} e todo o acompanhamento serão excluídos definitivamente. Esta ação não pode ser desfeita.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <DialogClose asChild>
            <Button variant="outline" disabled={removendo}>Cancelar</Button>
          </DialogClose>
          <Button variant="destructive" onClick={excluir} disabled={removendo}>
            {removendo ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Trash2Icon className="mr-2 h-4 w-4" />} Excluir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </div>
  )
}